import { Component } from '@angular/core';
import { OnInit } from '@angular/core';
import { UserService } from '../../services/user.service';
import { AlertsService } from '../../services/alerts.service';
import cloneDeep from 'lodash/cloneDeep';

/**
 * Component for managing a user's field preferences.
 */
@Component({
	selector: 'user-fields',
	templateUrl: './fields.component.html',
	styleUrl: './fields.component.css'
})
export class FieldsComponent implements OnInit {

	LOCALIZATION: any;

	fieldPrefs: any[] = [];

	all_results: boolean = false;
	all_search: boolean = false;
	all_metadata: boolean = false;

	// Field reordering
	searchFieldOrderPrefs: any[] = [];
	resultFieldOrderPrefs: any[] = [];
	dragIndex: number = -1;
	dragContext: string | undefined;
	beforeDragIndex: number = -1;

	activeTabInFields: string = 'fields';

	constructor(
		private alertsService: AlertsService,
		private userService: UserService
	) { }

	ngOnInit(): void {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.loadSearchOpts();
	}

	private loadSearchOpts(): void {
		this.fieldPrefs = cloneDeep(this.userService.user.properties.awfieldObjectList);

		this.checkAllResults();
		this.checkAllSearch();
		this.checkAllMetadata();

		this.setFieldOrder();
	}

	private setFieldOrder(): void {
		this.searchFieldOrderPrefs = cloneDeep(this.userService.user.properties.awfieldObjectList);
		this.resultFieldOrderPrefs = cloneDeep(this.userService.user.properties.awfieldObjectList);
		
		// Separate result fields that should not be reordered from the rest
		let sortableResultFields = this.resultFieldOrderPrefs.filter((col: any) => col.name !== 'title');
		
		// TODO: DRY - add service method to sort input based on retrieved prefs
		this.userService.getUserExtendedPrefs().then(
			(userPrefsObj: any) => {
				// Reorder the fields based on these settings
				let prefs: any = {};
				try {
				  const rawPrefs = userPrefsObj.prefs;
				  prefs = JSON.parse(rawPrefs);
				} catch (e) {
				  console.error('Invalid JSON in userPrefsObj.prefs:', e);
				  prefs = {}; // fallback to empty object
				}
				
				var searchorder = prefs.searchFieldOrder || [];
				var resultorder = prefs.resultFieldOrder || [];

				this.searchFieldOrderPrefs.sort((a, b) => {
					const indexA = searchorder.indexOf(a.name);
					const indexB = searchorder.indexOf(b.name);

					if (indexA === -1 && indexB === -1) {
						return 0;
					} else if (indexA === -1) {
						return 1;
					} else if (indexB === -1) {
						return -1;
					} else {
						return indexA - indexB;
					}
				});
				
				sortableResultFields.sort((a, b) => {
					const indexA = resultorder.indexOf(a.name);
					const indexB = resultorder.indexOf(b.name);

					if (indexA === -1 && indexB === -1) {
						return 0;
					} else if (indexA === -1) {
						return 1;
					} else if (indexB === -1) {
						return -1;
					} else {
						return indexA - indexB;
					}
				});
				
				this.resultFieldOrderPrefs = [...sortableResultFields];
			},
			(err: any) => {
				console.error("Failed to fetch user extended details.", err);
			}			
		);
	}

	checkAllResults(): void {
		if (this.fieldPrefs && this.fieldPrefs.length > 0) {
			var all = true;
			for (var i = 0; i < this.fieldPrefs.length; i++) {
				if (!this.fieldPrefs[i].isResult) {
					all = false;
					break;
				}
			}
			this.all_results = all;
		}
	}

	toggleAllResult(): void {
		for (let i = 0; i < this.fieldPrefs.length; i++) {
			this.fieldPrefs[i].isResult = !this.all_results;
		}
	}

	checkAllSearch(): void {
		if (this.fieldPrefs && this.fieldPrefs.length > 0) {
			var all = true;
			for (var i = 0; i < this.fieldPrefs.length; i++) {
				if (!this.fieldPrefs[i].isSearch) {
					all = false;
					break;
				}
			}
			this.all_search = all;
		}
	}

	toggleAllSearch(): void {
		for (let i = 0; i < this.fieldPrefs.length; i++) {
			this.fieldPrefs[i].isSearch = !this.all_search;
		}
	}

	checkAllMetadata(): void {
		if (this.fieldPrefs && this.fieldPrefs.length > 0) {
			var all = true;
			for (var i = 0; i < this.fieldPrefs.length; i++) {
				if (!this.fieldPrefs[i].isMetadata) {
					all = false;
					break;
				}
			}
			this.all_metadata = all;
		}
	}

	toggleAllMetadata(): void {
		for (let i = 0; i < this.fieldPrefs.length; i++) {
			this.fieldPrefs[i].isMetadata = !this.all_metadata;
		}
	}

	public saveChanges(): void {
		if (this.activeTabInFields === 'fields') {
			console.log('active tab fields');
			this.updatePreferences();
		}
		if (this.activeTabInFields === 'order') {
			console.log('active tab order');
			this.updateFieldOrder();
		}
	}

	updatePreferences(): void {
		let prefs = {
			awpropertyObjectList: this.userService.user.properties.awpropertyObjectList,
			awsortObjectList: this.userService.user.properties.awsortObjectList,
			awfieldObjectList: this.fieldPrefs,
			awfieldCollectionObjectList: this.userService.user.properties.awfieldCollectionObjectList,
			awlibraryObjectList: this.userService.user.properties.awlibraryObjectList
		};

		let cleanedPrefs = this.userService.cleanData(prefs);

		let payload = {
			prefs: JSON.stringify(cleanedPrefs)
		};

		this.userService.updateUser(payload).then(
			(response: any) => {
				this.alertsService.success({
					message: this.LOCALIZATION.userProfileUpdateSuccess,
					delay: 5000
				});
			},
			(err: any) => {
				console.error("Failed to update user details.", err);
				this.alertsService.error({
					message: this.LOCALIZATION.userProfileUpdateError,
					delay: 5000
				});
			}
		);
	}

	trackByIndex(index: number, _item: any): number {
		return index;
	}

	dragActions(action: string, context: string, fieldIndex: number, event: DragEvent): void {
		event.preventDefault();

		const eleDragAllowed = (event.currentTarget as HTMLElement)?.getAttribute('draggable');
		if (eleDragAllowed !== 'true') return;

		this.dragContext = context;

		if (action === 'enter') {
			this.dragIndex = fieldIndex;
		} else if (action === 'leave') {
			this.beforeDragIndex = this.dragIndex;
			this.dragIndex = fieldIndex === this.dragIndex ? -1 : this.dragIndex;
		} else if (action === 'end') {
			let model = this.searchFieldOrderPrefs;
			if (context === 'result') {
				model = this.resultFieldOrderPrefs;
			}

			if (fieldIndex >= 0 && fieldIndex < model.length) {
				const tempField = model[fieldIndex];
				model.splice(fieldIndex, 1);

				if (this.dragIndex >= 0) {
					model.splice(this.dragIndex, 0, tempField);
				} else if (this.beforeDragIndex >= 0) {
					model.splice(this.beforeDragIndex, 0, tempField);
				}
			}

			this.dragIndex = -1;
			this.beforeDragIndex = -1;
		}
	}

	// The field order is maintained separately from user prefs
	updateFieldOrder(): void {
		var sortprefs = {
			searchFieldOrder: (this.searchFieldOrderPrefs ? this.searchFieldOrderPrefs.map((field: any) => { return field.name }) : []),
			resultFieldOrder: (this.resultFieldOrderPrefs ? this.resultFieldOrderPrefs.map((field: any) => { return field.name }) : [])
		};

		this.userService.updateUserExtendedPrefs(sortprefs).then(
			(saved: boolean) => {
				if (saved) {
					this.alertsService.success({
						message: this.LOCALIZATION.userProfileUpdateSuccess,
						delay: 5000
					});
				} else {
					// User did not have permission to save, so we issue no alerts
				}
			},
			(err: any) => {
				console.error("Failed to update user extended details.", err);
				this.alertsService.error({
					message: this.LOCALIZATION.userProfileUpdateError,
					delay: 5000
				});
			}
		);
	}
}
